import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { PdfEditModal } from "./pdf-edit-modal";
import { useQueryClient } from "@tanstack/react-query";
import { conversionsKeys } from "@/features/dashboard/queries/conversions.queries";
import { useExpand } from "@/hooks/use-expand";
import * as pdfjs from "pdfjs-dist";
import { ArrowRight, Info, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ACCEPTED_FILE_TYPES = {
  "image/png": [".png"],
  "application/pdf": [".pdf"],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const MAX_FILES = 5;

interface FileUploaderProps {
  countryId: string;
  gradingScaleId: string;
}

export function FileUploader({ countryId, gradingScaleId }: FileUploaderProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [fileToEdit, setFileToEdit] = useState<File | null>(null);
  const [fileToEditIndex, setFileToEditIndex] = useState<number | null>(null);
  const [filePageCounts, setFilePageCounts] = useState<{
    [key: string]: number;
  }>({});
  const queryClient = useQueryClient();
  const { setIsExpanded } = useExpand();

   const isCustomScalesMode = (!countryId || countryId === "") && gradingScaleId && gradingScaleId !== "";
  
   const isValidationPassed = isCustomScalesMode ? (!!gradingScaleId && gradingScaleId !== "") : (!!countryId && countryId !== "" && !!gradingScaleId && gradingScaleId !== "");

  const getFileKey = (file: File, index: number) =>
    `${file.name}-${file.size}-${index}`;

  const countPdfPages = async (file: File, index: number) => {
    if (file.type !== "application/pdf") return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;

      setFilePageCounts((prev) => ({
        ...prev,
        [getFileKey(file, index)]: pageCount,
      }));
    } catch (error) {
      console.error("Error counting PDF pages:", error);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      const rejectionErrors = rejectedFiles.map((rejection) => {
        const file = rejection.file;
        const error = rejection.errors[0];

        if (error.code === "file-too-large") {
          toast.error(`"${file.name}" is too large. Max size is 5MB.`);
          return `"${file.name}" is too large. Max size is 5MB.`;
        }

        if (error.code === "file-invalid-type") {
          toast.error(
            `"${file.name}" has invalid file type. Only PNG and PDF files are accepted.`
          );
          return `"${file.name}" has invalid file type. Only PNG and PDF files are accepted.`;
        }

        toast.error(`"${file.name}" - ${error.message}`);
        return `"${file.name}" - ${error.message}`;
      });

      if (rejectionErrors.length > 0) {
        setErrors(rejectionErrors);
      }

      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );

      if (files.length + newFiles.length > MAX_FILES) {
        setErrors([`You can only upload a maximum of ${MAX_FILES} files.`]);
        toast.error(`You can only upload a maximum of ${MAX_FILES} files.`);

        const availableSlots = MAX_FILES - files.length;
        if (availableSlots > 0) {
          const filesToAdd = newFiles.slice(0, availableSlots);
          setFiles((prev) => {
            const updatedFiles = [...prev, ...filesToAdd];

            filesToAdd.forEach((file, fileIndex) => {
              const actualIndex = prev.length + fileIndex;
              countPdfPages(file, actualIndex);
            });
            return updatedFiles;
          });
        }
      } else {
        setFiles((prev) => {
          const updatedFiles = [...prev, ...newFiles];
          newFiles.forEach((file, fileIndex) => {
            const actualIndex = prev.length + fileIndex;
            countPdfPages(file, actualIndex);
          });
          return updatedFiles;
        });
        setErrors([]);
      }
    },
    [files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES - files.length > 0 ? MAX_FILES - files.length : 1,
    disabled: !isValidationPassed,
  });

  const removeFile = (index: number) => {
    setFiles((files) => {
      const newFiles = [...files];
      const removedFile = files[index];

      // Clean up page count data for removed file
      setFilePageCounts((prev) => {
        const newPageCounts = { ...prev };
        delete newPageCounts[getFileKey(removedFile, index)];
        return newPageCounts;
      });

      newFiles.splice(index, 1);
      return newFiles;
    });
    setErrors([]);
  };

  const openEditModal = (file: File, index: number) => {
    if (file.type === "application/pdf") {
      setFileToEdit(file);
      setFileToEditIndex(index);
      setIsEditModalOpen(true);
    } else {
      toast.info("Editing is only supported for PDF files.");
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setFileToEdit(null);
    setFileToEditIndex(null);
  };

  const handlePdfSave = (newFile: File) => {
    if (fileToEditIndex !== null) {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        updatedFiles[fileToEditIndex] = newFile;
        return updatedFiles;
      });
    }
    closeEditModal();
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error("Please add files to upload.");
      return;
    }

    setUploading(true);
    setErrors([]);

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

     if (isCustomScalesMode) {
      formData.append("isCustomScale", "true");
      formData.append("scaleId", gradingScaleId);
     } else {
      if (countryId && countryId !== "") {
        formData.append("countryId", countryId);
      }
      formData.append("scaleId", gradingScaleId);
    }

    try {
      await api.post("/api/conversion/ai", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setIsExpanded(true);

      toast.success("Transcript uploaded successfully");

      queryClient.invalidateQueries({
        queryKey: conversionsKeys.all,
      });

      setFiles([]);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.log("Upload failed:", error.response?.data.message);
        toast.error(error.response?.data.message);
      } else {
        console.log("Upload failed:", error);
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="w-full"
      onClick={() => {
        if (!isValidationPassed) {
          if (isCustomScalesMode) {
            toast.warning("Please select a custom grading scale first.");
          } else {
            toast.warning("Please select a country and grading scale first.");
          }
          return;
        }
      }}
    >
      <div
        id="upload-transcript"
        {...getRootProps()}
        className={`p-6 border-2 border-dashed rounded-lg cursor-pointer transition duration-200 ease-in-out ${
          isDragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-blue-400"
        } ${files.length >= MAX_FILES ? "opacity-50 pointer-events-none" : ""}`}
      >
        <input {...getInputProps()} />
        {files.length >= MAX_FILES ? (
          <p className="text-center text-gray-500">
            Maximum number of files reached
          </p>
        ) : (
          <>
            {isDragActive ? (
              <p className="text-center text-blue-500">
                Drop the files here...
              </p>
            ) : (
              <div className="text-center">
                <p className="mb-2 text-gray-600">
                  Drag & drop files here, or click to select files
                </p>
                <p className="text-xs text-gray-500">
                  PNG and PDF files only (max {MAX_FILES} files, 5MB each)
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {files.length > 0
                    ? `${files.length} file${
                        files.length > 1 ? "s" : ""
                      } selected (${MAX_FILES - files.length} slot${
                        MAX_FILES - files.length !== 1 ? "s" : ""
                      } remaining)`
                    : `No files selected (${MAX_FILES} slots available)`}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {errors.length > 0 && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <ul className="text-red-600 text-sm">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Selected Files:
          </h3>
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200"
              >
                <div className="flex items-center flex-1 min-w-0 mr-2">
                  <div className="overflow-hidden">
                    <p
                      className="text-sm font-medium text-gray-900 truncate"
                      title={file.name}
                    >
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {file.type === "application/pdf" && (
                    <>
                      {filePageCounts[getFileKey(file, index)] > 1 && (
                        <Badge variant="pending" className="text-xs p-2">
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="w-4 h-4" />
                              </TooltipTrigger>
                              <TooltipContent>
                                For accurate results, please edit the PDF to
                                remove any unnecessary pages.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          Needs Edit{" "}
                          <ArrowRight className="w-4 h-4" strokeWidth={3} />
                        </Badge>
                      )}
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(file, index);
                        }}
                        id="edit-button"
                        variant={"outline"}
                        size="sm"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        disabled={uploading}
                      >
                        Edit
                      </Button>
                    </>
                  )}

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    variant={"link"}
                    size={"icon"}
                    className="text-red-500 cursor-pointer"
                    disabled={uploading}
                  >
                    <X />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Button
              onClick={uploadFiles}
              disabled={uploading}
              className={`w-full py-2 px-4 rounded-md text-white font-medium`}
            >
              {uploading ? "Uploading..." : "Upload Files"}
            </Button>
          </div>
        </div>
      )}

      <PdfEditModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        file={fileToEdit}
        onSave={handlePdfSave}
      />
    </div>
  );
}
